import React, { useState, useRef, useEffect, useContext } from "react";
import threeDots from "../../../../../../assets/threeDots.svg";
import { MessageContext } from "../../../Message.tsx";

export interface OptionItem {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    tooltip?: string;
    isDisabled?: boolean;
}
interface OptionsMenuProps {
    options: OptionItem[];
    align?: 'left' | 'right';
}
const OptionsMenu: React.FC<OptionsMenuProps> = ({ options, align = 'right' }) => {
    const { isPreview } = useContext(MessageContext);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                className={`brightness-[0.97] dark:brightness-[0.91] w-4 h-4 flex items-center justify-center ${isPreview ? 'opacity-50' : 'cursor-pointer'}`}
                onClick={isPreview ? undefined : toggleMenu}
                disabled={isPreview}
                data-tooltip-id="main-tooltip"
                data-tooltip-content="More options"
                data-tooltip-place="top"
            >
                <img
                    src={threeDots}
                    alt="More"
                    className={`h-4 w-4 mt-0.5 invert-0 dark:invert ${isPreview ? 'filter grayscale' : ''}`}
                />
            </button>

            {!isPreview && (
                <div
                    className={`absolute bottom-[105%] ${align === 'right' ? 'right-0' : 'left-0'} transform transition-all duration-200 ease-out ${
                        isMenuOpen 
                            ? 'opacity-100 translate-y-0' 
                            : 'opacity-0 translate-y-1 pointer-events-none'
                    }`}
                >
                    <div
                        ref={menuRef}
                        className="w-28 bg-white/95 dark:bg-neutral-900/95 rounded-md shadow-md border border-neutral-300 dark:border-neutral-600 backdrop-blur-sm"
                    >
                        <div>
                            {options.map((option, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        if (!option.isDisabled) {
                                            option.onClick();
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                    className={`w-full text-left px-2.5 py-1.5 text-sm flex items-center justify-between transition-colors duration-150
                                        ${option.isDisabled 
                                            ? 'opacity-50 cursor-default' 
                                            : 'text-black dark:text-white hover:bg-neutral-200 dark:hover:bg-neutral-700/70 cursor-pointer'
                                        }
                                        ${index !== options.length - 1 ? 'border-b border-neutral-200 dark:border-neutral-700' : ''}`}
                                    data-tooltip-id="main-tooltip"
                                    data-tooltip-content={option.tooltip}
                                    data-tooltip-place="left"
                                >
                                    {option.label}
                                    <span className="ml-3">{option.icon}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OptionsMenu;
